// src/components/About.js
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

// Add slide-in animation
const slideInFromLeft = keyframes`
  0% {
    transform: translateX(-100%);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

// Reuse the PageContainer and Canvas styles from Header.js
const PageContainer = styled.div`
  position: relative;
  min-height: 100vh;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
`;

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const AboutContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2rem;
  color: #00ff00;
  font-family: 'Courier New', monospace;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  padding: 2rem;
  z-index: 2;
  width: 95%;
  max-width: 1200px;
  margin: 2rem;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 95%;
    padding: 1rem;
    margin: 1rem;
    gap: 1rem;
  }
`;

const ProfileImage = styled.img`
  width: 400px;
  height: 400px;
  border: 2px solid #00ff00;
  border-radius: 50%;
  box-shadow: 0 0 10px rgba(0, 255, 0, 0.3);
  opacity: 0;
  animation: ${slideInFromLeft} 1s ease-out forwards;
  animation-play-state: paused;
  margin-top: 3rem;
  object-fit: cover;
  object-position: center top;
  
  &.visible {
    animation-play-state: running;
  }
  
  @media (max-width: 768px) {
    width: 250px;
    height: 250px;
    border-radius: 50%;
  }
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const About = () => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const aboutRef = useRef(null);
  const aboutText = 'About Me';
  const descriptionText = (
    <div style={{ 
      textAlign: 'center',
      maxWidth: '1000px',
      margin: '2rem auto',
      padding: '2rem',
      border: '1px solid #00ff00',
      borderRadius: '10px',
      boxShadow: '0 0 10px #00ff00',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
    }}>
      {[
        {
          intro: `I'm Jack Shkifati, an enthusiastic and innovative Computer Engineer hailing from the vibrant city of New York. When I'm not pouring over data structures or tinkering with microcontrollers, you might find me exploring the city's food scene, diving into the latest physics book, or on the quest for the perfect espresso`,
        },
        {
          passion: `Ever since I can remember, I've had a deep-rooted passion for understanding how things work. I'm not just talking about software and electronics, but the fundamental laws that govern our universe. Physics has always been a subject close to my heart and understanding these principles gives me an unparalleled appreciation for the technology I work with.`,
        },
        {
          education: `I'm a proud alumni of NYU's Tandon School of Engineering and CUNY's Brooklyn College, where I was able to foster my love for engineering and physics alike. From C++ to Python, Arduino to Raspberry Pi, and databases to machine learning, I've got quite the technological arsenal up my sleeve!`,
        },
        {
          career: `In the realm of technology, innovation, and impact, I've found my calling. Throughout my career, I've worked on diverse projects — from developing a bio-security system to predicting breast cancer survivability. And trust me, there's no thrill quite like the one you get when you see your code come to life!`,
        },
        {
          achievements: `On the professional front, I've made significant contributions as an Embedded Software Engineer, where I developed a device for non-invasive detection of skin abnormalities, enhancing accuracy in skin cancer screening. It's these projects where tech meets impact that get me really excited!`,
        },
      ].map(section => Object.values(section)[0]).join('\n\n')}
    </div>
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    
    // Function to handle resize
    const handleResize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
    };

    // Initial setup
    handleResize();
    
    const columns = Math.floor(window.innerWidth / 10);
    const drops = Array(columns).fill(1);

    const draw = () => {
        context.fillStyle = 'rgba(0, 0, 0, 0.05)';
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.fillStyle = '#00ff00';
        context.font = '10px monospace';

        drops.forEach((y, index) => {
            const text = String.fromCharCode(Math.floor(Math.random() * 128));
            const x = index * 10;
            context.fillText(text, x, y * 10);

            if (y * 10 > canvas.height && Math.random() > 0.975) {
                drops[index] = 0;
            }
            drops[index]++;
        });
    };

    const interval = setInterval(draw, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.5,
      }
    );

    observer.observe(imageRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-slide-in'); // Add animation class
          observer.unobserve(entry.target); // Stop observing after animation
        }
      });
    });

    if (aboutRef.current) {
      observer.observe(aboutRef.current); // Observe the about section
    }

    return () => {
      if (aboutRef.current) {
        observer.unobserve(aboutRef.current); // Clean up observer
      }
    };
  }, []);

  return (
    <PageContainer ref={aboutRef}>
      <Canvas ref={canvasRef} />
      <AboutContainer>
        <ProfileImage src="/jack2.png" alt="Profile" ref={imageRef} />
        <TextContainer>
          <Title>{aboutText}</Title>
          {descriptionText}
        </TextContainer>
      </AboutContainer>
    </PageContainer>
  );
};

export default About;
